import React, {
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";
import { formatDateLabel } from "@shared/utils/date";

export interface BalancePoint {
  /** Unix seconds. */
  time: number;
  value: number;
}

interface BalanceChartProps {
  points: BalancePoint[];
  height: number;
  /** Day-long ranges label hours; longer ones label dates. */
  intraday: boolean;
  /** Points are hourly snapshots: the crosshair's date carries its hour. */
  hourly?: boolean;
  /** A guide's value; `step` is the distance between guides. */
  formatAxisValue: (value: number, step: number) => string;
  ariaLabel: string;
  /** Given, the hovered point's value floats above it. */
  formatValue?: (value: number) => string;
  /** The point under the crosshair (undefined when it leaves). */
  onActiveChange?: (point?: BalancePoint) => void;
}

const AXIS_WIDTH = 52;
const TOP = 10;
const BOTTOM = 22;
// The value chip's height and its gap above the point.
const VALUE_CHIP_OFFSET = 30;

/**
 * The portfolio's balance over the selected range: a smooth line with a soft
 * fill and three dotted value guides on the right. The crosshair (pointer
 * or arrow keys) marks a point and its time on the axis; the panel's headline
 * reads out the value, and with `formatValue` so does a chip over the point.
 */
const BalanceChart = ({
  points,
  height,
  intraday,
  hourly = false,
  formatAxisValue,
  ariaLabel,
  formatValue,
  onActiveChange,
}: BalanceChartProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const gradientId = `balance-${useId().replace(/[^\w-]/g, "")}`;
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const [active, setActive] = useState<number>();

  const plotWidth = Math.max(0, width - AXIS_WIDTH);
  const plotHeight = height - TOP - BOTTOM;

  const shape = useMemo(() => {
    if (points.length < 2 || !plotWidth) return undefined;
    let min = Math.min(...points.map((p) => p.value));
    let max = Math.max(...points.map((p) => p.value));
    if (max === min) {
      const pad = Math.abs(max) * 0.01 || 1;
      min -= pad;
      max += pad;
    } else {
      const pad = (max - min) * 0.12;
      min -= pad;
      max += pad;
    }
    const t0 = points[0].time;
    const t1 = points[points.length - 1].time;
    const x = (time: number) =>
      t1 === t0 ? 0 : ((time - t0) / (t1 - t0)) * plotWidth;
    const y = (value: number) =>
      TOP + (1 - (value - min) / (max - min)) * plotHeight;
    const coords: Point[] = points.map((p) => [x(p.time), y(p.value)]);
    const line = monotonePath(coords);
    const last = coords[coords.length - 1];
    const area = `${line}L${last[0]},${TOP + plotHeight}L0,${
      TOP + plotHeight
    }Z`;
    const guides = [0.15, 0.5, 0.85].map((share) => {
      const value = max - (max - min) * share;
      return { y: y(value), value };
    });
    const guideStep = (max - min) * 0.35;
    const ticks = [0, 0.33, 0.66, 1].map((share) => {
      const time = t0 + (t1 - t0) * share;
      return { x: x(time), time, share };
    });
    return { coords, line, area, guides, guideStep, ticks };
  }, [points, plotWidth, plotHeight]);

  const onPointer = (clientX: number, element: HTMLElement) => {
    if (!shape) return;
    const left = element.getBoundingClientRect().left;
    const px = clientX - left;
    let nearest = 0;
    shape.coords.forEach(([cx], index) => {
      if (Math.abs(cx - px) < Math.abs(shape.coords[nearest][0] - px))
        nearest = index;
    });
    setActive(nearest);
  };

  const formatTime = (time: number) =>
    formatDateLabel(moment.unix(time), intraday ? "HH:mm" : "MMM D");
  const formatTooltipTime = (time: number) =>
    formatDateLabel(
      moment.unix(time),
      intraday ? "MMM D, HH:mm" : hourly ? "MMM D, YYYY, HH:mm" : "MMM D, YYYY"
    );

  const activePoint = active !== undefined ? points[active] : undefined;
  useEffect(() => {
    onActiveChange?.(activePoint);
  }, [activePoint, onActiveChange]);
  const activeCoord =
    active !== undefined && shape ? shape.coords[active] : undefined;
  const lastCoord = shape?.coords[shape.coords.length - 1];

  return (
    <Wrapper
      ref={measureRef}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
      tabIndex={shape ? 0 : -1}
      onMouseMove={(event) => onPointer(event.clientX, event.currentTarget)}
      onMouseLeave={() => setActive(undefined)}
      onBlur={() => setActive(undefined)}
      onKeyDown={(event) => {
        if (!shape) return;
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          const step = event.key === "ArrowLeft" ? -1 : 1;
          setActive((current) =>
            Math.min(
              shape.coords.length - 1,
              Math.max(0, (current ?? shape.coords.length - 1) + step)
            )
          );
        }
      }}
    >
      {shape && (
        <svg width={width} height={height} aria-hidden={true}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={tokens.accent}
                stopOpacity={theme.isDarkMode ? 0.22 : 0.14}
              />
              <stop offset="100%" stopColor={tokens.accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          {shape.guides.map((guide) => (
            <g key={guide.y}>
              <line
                x1={0}
                x2={plotWidth}
                y1={guide.y}
                y2={guide.y}
                stroke={tokens.hairlineStrong}
                strokeDasharray="2 4"
              />
              <AxisText x={plotWidth + 10} y={guide.y + 4} fill={tokens.muted}>
                {formatAxisValue(guide.value, shape.guideStep)}
              </AxisText>
            </g>
          ))}
          {shape.ticks.map((tick) => (
            <AxisText
              key={tick.share}
              x={tick.x}
              y={height - 6}
              fill={tokens.muted}
              textAnchor={
                tick.share === 0 ? "start" : tick.share === 1 ? "end" : "middle"
              }
            >
              {formatTime(tick.time)}
            </AxisText>
          ))}
          <path d={shape.area} fill={`url(#${gradientId})`} />
          <path
            d={shape.line}
            fill="none"
            stroke={tokens.accent}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {lastCoord && !activeCoord && (
            <circle
              cx={lastCoord[0]}
              cy={lastCoord[1]}
              r={3.5}
              fill={tokens.accent}
              stroke={tokens.panel}
              strokeWidth={2}
            />
          )}
          {activeCoord && (
            <g>
              <line
                x1={activeCoord[0]}
                x2={activeCoord[0]}
                y1={TOP}
                y2={TOP + plotHeight}
                stroke={hexToRGB(tokens.ink, theme.isDarkMode ? 0.35 : 0.25)}
                strokeWidth={1}
              />
              <circle
                cx={activeCoord[0]}
                cy={activeCoord[1]}
                r={4.5}
                fill={tokens.accent}
                stroke={tokens.panel}
                strokeWidth={2}
              />
            </g>
          )}
        </svg>
      )}
      {activePoint && activeCoord && formatValue && (
        <Chip
          x={activeCoord[0]}
          maxX={plotWidth}
          // Above the point, or under it when the line runs along the top.
          top={
            activeCoord[1] - VALUE_CHIP_OFFSET < 0
              ? activeCoord[1] + 10
              : activeCoord[1] - VALUE_CHIP_OFFSET
          }
          Styled={ValueChip}
        >
          {formatValue(activePoint.value)}
        </Chip>
      )}
      {activePoint && activeCoord && (
        <Chip
          x={activeCoord[0]}
          maxX={plotWidth}
          top={height - BOTTOM + 3}
          Styled={TimeChip}
        >
          {formatTooltipTime(activePoint.time)}
        </Chip>
      )}
    </Wrapper>
  );
};

/** A chip centred on `x`, kept whole inside 0…`maxX`. */
const Chip = ({
  x,
  maxX,
  top,
  Styled,
  children,
}: {
  x: number;
  maxX: number;
  top: number;
  Styled: typeof TimeChip;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [half, setHalf] = useState(48);
  useLayoutEffect(() => {
    if (ref.current) setHalf(ref.current.offsetWidth / 2);
  }, [children]);
  return (
    <Styled
      ref={ref}
      style={{ left: Math.min(Math.max(x, half), maxX - half), top }}
    >
      {children}
    </Styled>
  );
};

export default React.memo(BalanceChart);

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  outline: none;
  cursor: crosshair;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    border-radius: 6px;
  }
`;
const AxisText = styled.text`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;
// Sits on the time axis under the crosshair, over the regular tick labels.
const TimeChip = styled.div`
  position: absolute;
  transform: translateX(-50%);
  padding: 1px 8px;
  border-radius: 6px;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  color: ${(p) => (p.theme.isDarkMode ? "#0A0E1C" : "#FFFFFF")};
  ${newLookType.meta};
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
`;
// Floats over the hovered point with its value.
const ValueChip = styled(TimeChip)`
  padding: 2px 8px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;
